import { useCallback, useEffect, useState, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import * as Analytics from 'analytics/analytics'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { ModalSteps } from '../defaults'
import TravelPreferencesOptionsGrid from '../TravelPreferencesImageItemsGrid/TravelPreferencesOptionsGrid'
import { getCurrentUserId } from 'selectors/accountSelectors'
import type { UserPreferenceHolidayOrBrands } from '../types'
import { deleteUserPreferenceExternalBrand, updateUserPreferenceExternalBrand } from 'actions/userTravelPreferencesActions'

interface Props {
  defaultDisplayExternelBrands?: Array<App.DefaultUserPreferenceExternalBrand>
  selectedExternelBrands?: Array<App.UserPreferenceExternalBrand>
  onSetNextStep: (step: ModalSteps, isSaveAction?: boolean) => void
}

function TravelPreferencesModalHotelBrands(props: Props) {
  const intl = useIntl()
  const { defaultDisplayExternelBrands, selectedExternelBrands, onSetNextStep } = props

  const dispatch = useAppDispatch()
  const userId = useAppSelector(getCurrentUserId)
  const initialSelectedIds: Set<string> = useMemo(() => new Set(selectedExternelBrands?.map(h => h.externalBrandId) || []), [selectedExternelBrands])
  const displayItems: Array<UserPreferenceHolidayOrBrands> | undefined = useMemo(() => (
    defaultDisplayExternelBrands?.map(i => ({ name: i.externalBrandDisplayName, id: i.externalBrandId, image: i.imageId }))
  ), [defaultDisplayExternelBrands])
  const [selectedHotelBrands, setSelectedHotelBrands] = useState<Set<string>>(new Set([]))

  useEffect(() => {
    if (selectedExternelBrands) {
      setSelectedHotelBrands(new Set(selectedExternelBrands.map(h => h.externalBrandId)))
    }
  }, [selectedExternelBrands])

  const onToggleSelection = useCallback((item: UserPreferenceHolidayOrBrands) => {
    setSelectedHotelBrands(prevSelected => {
      const newSelected = new Set(prevSelected)

      if (newSelected.has(item.id)) {
        newSelected.delete(item.id)
      } else {
        newSelected.add(item.id)
      }

      return newSelected
    })
  }, [])

  const onSkip = useCallback(() => {
    onSetNextStep(ModalSteps.travellers)
  }, [onSetNextStep])

  const onUpdatePreferences = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'hotel_brands',
      action: 'completion',
      type: 'interaction',
      category: 'travel_preferences',
    })

    // compare the current state and redux state, and then send the requests
    for (const id of selectedHotelBrands) {
      if (!initialSelectedIds.has(id)) {
        dispatch(updateUserPreferenceExternalBrand(userId!, id))
      }
    }
    for (const id of initialSelectedIds) {
      if (!selectedHotelBrands.has(id)) {
        dispatch(deleteUserPreferenceExternalBrand(userId!, id))
      }
    }

    onSetNextStep(ModalSteps.travellers, true)
  }, [dispatch, onSetNextStep, selectedHotelBrands, initialSelectedIds, userId])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'hotel_brands',
      action: 'impression',
      type: 'nonInteraction',
      category: 'travel_preferences',
    })
  }, [])

  return (
    <>
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={16}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'mAWq28nK6aGa2S5HyFAm',
              defaultMessage: 'Enjoy priority access to exclusive offers that suit your preferences. Select up to five brands.',
              description: 'Description for hotel brands step in travel preferences modal',
            })}
            </BodyText>
            <TravelPreferencesOptionsGrid
              showLabel={false}
              items={displayItems || []}
              onSelect={onToggleSelection}
              selectedId={selectedHotelBrands}
            />
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter primaryActionProps={{ children: intl.formatMessage({ id: 'Gjrj-QYmC4qA1jg5Gfgd', defaultMessage: 'Save & continue', description: 'Button to save hotel brands and continue to next step' }), onClick: onUpdatePreferences }} secondaryActionProps={{ children: intl.formatMessage({ id: 'At1KjWbDaerm0F9I3SSw', defaultMessage: 'Skip', description: 'Button to skip hotel brands step on the hotel or room booking page' }), onClick: onSkip }} />
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  defaultDisplayExternelBrands: state.userTravelPreferences.defaultExternalBrands.data,
  selectedExternelBrands: state.userTravelPreferences.externalBrands.data,
})

export default connect(mapStateToProps)(TravelPreferencesModalHotelBrands)
